import React from 'react';
import axios from 'axios';
import { findIndex } from 'lodash';
import { notification, Button } from 'antd';
let requests = [];
let storeReauthShown = false;

const navigateWithoutReload = (to) => {
  try {
    window.history.pushState({}, '', to);
    window.dispatchEvent(new PopStateEvent('popstate'));
  } catch (e) {
    window.location.href = to;
  }
};

const clearRequestArray = () => {
  requests = [];
};

export const resetStoreReauthFlag = () => {
  storeReauthShown = false;
  sessionStorage.removeItem('storeReauthDismissed');
};

export default clearRequestArray;

export const pendingRequestInterceptors = [
  (req) => {
    const { url } = req;
    const method = req.method.toUpperCase();
    const urlToCheck = url.toString();

    // Build a unique key that includes query params so requests to the same
    // endpoint with different params (e.g. different shipmentId) are NOT
    // treated as duplicates and cancelled.
    const paramStr = req.params ? JSON.stringify(req.params) : '';
    const requestKey = `${url}|${paramStr}`;

    // HANDLING PREVIOUS REQUEST AND CANCELLING IF STILL PANDING
    const index = findIndex(requests, (request) => request.key == requestKey && request.method == method);
    if (index != '-1' && method == 'GET' &&
      !urlToCheck.includes('/api/v2/seller_skus/vendors') &&
      !(urlToCheck.includes('s3')) &&
      !(urlToCheck.includes('presigned_url'))
    ) {
      const { cancel } = requests[index];
      requests.splice(index, 1);
      cancel('Pending Request cancel due to new request');
    }

    // HANDLING CURRENT REQUEST AND CREATING CANCEL TOKEN
    if (method == 'GET' && !urlToCheck.includes('/api/v2/seller_skus/vendors') && !(urlToCheck.includes('s3')) && !(urlToCheck.includes('presigned_url'))) {
      req.cancelToken = new axios.CancelToken((cancel) => {
        requests.push({
          key: requestKey,
          url,
          method,
          cancel
        });
      });
    }

    if (urlToCheck.includes('/api/v2/users/logged_in_user')) req.signal = null;

    return req;
  },
  (error) => {
    return Promise.reject(error);
  }
];

export const pendingRsponseInterceptors = [
  (res) => {
    const { config } = res;
    const { url } = config;
    const method = config.method.toUpperCase();

    // Build the same key used in the request interceptor
    const paramStr = config.params ? JSON.stringify(config.params) : '';
    const requestKey = `${url}|${paramStr}`;

    // REMOVING REQUEST FROM ARRAY AFTER GETTING RESPONSE
    const index = findIndex(requests, (req) => req.key == requestKey && req.method == method);
    if (index != '-1') requests.splice(index, 1);

    const isDismissed = sessionStorage.getItem('storeReauthDismissed') === 'true';
    const isOnSignUpPage = window.location.pathname.includes('/auth/sign-up');

    const isCurrentUserRequest = typeof url === 'string' && url.includes('/users/current-user');
    const storeActive = res?.data?.current_user?.store_active;
    const isEmployee = res?.data?.current_user?.is_employee_of;
    const isWarehouse = res?.data?.current_user?.role_id == 3;

    if (isCurrentUserRequest && storeActive === true) {
      notification.destroy('store-reauth-notification');
      storeReauthShown = false;
    }

    if (isCurrentUserRequest && storeActive === false && !isDismissed && !isEmployee && !isWarehouse) {
      if (isOnSignUpPage) {
        notification.destroy('store-reauth-notification');
        storeReauthShown = false;
      } else if (!storeReauthShown) {
        storeReauthShown = true;

        // Defer toast until after initial opacity paint, so it doesn't look "jumpy" on load.
        const showReauthToast = () => {
          notification.warning({
            message: 'Store Connection Expired',
            style: {
              width: 481,
              transform: 'translateX(-97px)'
            },
            description: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ whiteSpace: 'nowrap' }}>Please reauthorize your Amazon store.</span>
                <Button
                  type="primary"
                  size="small"
                  style={{ backgroundColor: '#1565D8', borderColor: '#1565D8' }}
                  onClick={() => {
                    sessionStorage.setItem('storeReauthDismissed', 'true');
                    notification.destroy('store-reauth-notification');
                    navigateWithoutReload('/account');
                  }}
                >
                  Reconnect
                </Button>
              </div>
            ),
            top: 65,
            duration: 0,
            placement: 'topRight',
            key: 'store-reauth-notification',
            onClose: () => {
              sessionStorage.setItem('storeReauthDismissed', 'true');
            }
          });
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => window.requestAnimationFrame(showReauthToast));
        } else {
          setTimeout(showReauthToast, 100);
        }
      }
    }

    return res;
  },
  (error) => {

    // Skip 401 handling for specific routes
    const skip401HandlingRoutes = ['/auth/sign-in', '/auth/send-verification-mail', '/payment/user-billing-history', '/users/business-onboarding'];
    const requestUrl = error.config?.url || '';

    if (error.response?.status === 403 ) {
      if (error.response.data?.message === 'Permission denied'){
        // Reload ONCE to pick up freshly-changed permissions — but guard against an
        // infinite reload loop. Pages that fire a request for a forbidden resource on
        // every mount (e.g. the seller dashboard aggregates cross-domain endpoints an
        // employee may not have permission for) would otherwise reload endlessly:
        // mount -> 403 -> reload -> mount -> 403 -> reload. The flag lives in
        // sessionStorage so it survives the one reload, and is cleared on sign-out /
        // sign-in (sessionStorage.clear() in the 401 handler), so a genuine
        // permission change in a later session still triggers a single refresh.
        if (!sessionStorage.getItem('permissionDeniedReloaded')) {
          sessionStorage.setItem('permissionDeniedReloaded', 'true');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
        }
        else{
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      }

      return Promise.resolve();
    }
    if (error.response?.status === 401 && !skip401HandlingRoutes.some(route => requestUrl.includes(route))) {
      // Show session expired notification
      notification.error({
        message: 'Session Expired',
        description: 'Please sign in again.',
        placement: 'topRight',
        duration: 3
      });
      
      // Clear any existing tokens or user data from storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear reauth notification and reset flag
      notification.destroy('store-reauth-notification');
      storeReauthShown = false;

      // Clear the pending requests
      requests = [];
      
      // Redirect to sign-in page after a short delay for the toast to be visible
      setTimeout(() => {
        window.location.href = '/auth/sign-in';
      }, 1000);
      
      return Promise.resolve(); // Prevent further error handling
    }
    
    return Promise.reject(error);
  }
];
